"use client";

import { useCallback, useEffect, useState } from "react";
import Logo from "./Logo";
import Button from "./Button";
import TextField from "./TextField";
import { useConfirm } from "./ConfirmDialog";
import type { ImageRequest, RuleFile, RuleSummary } from "@/lib/image-gen";

const API = "/api/admin/image-gen";

const STATE_LABEL: Record<string, string> = {
  requested: "依頼中",
  done: "生成済み",
  failed: "失敗",
};

const KIND_LABEL: Record<string, string> = {
  new: "新規",
  fix: "修正",
};

function stampOf(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getMonth() + 1}/${d.getDate()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

const imageUrl = (row: ImageRequest, width?: number) =>
  `${API}/file?name=${encodeURIComponent(row.name)}${width ? `&w=${width}` : ""}&v=${row.image_mtime}`;

async function get(query: string) {
  const response = await fetch(`${API}?${query}`);
  const body = await response.json();
  if (!response.ok) throw new Error(body.error ?? "読めません");
  return body;
}

/** 依頼 1 件の中身。大きな絵と依頼の項目を出す */
function RequestDetail({ row, ruleTitle, onClose }: { row: ImageRequest; ruleTitle: string; onClose: () => void }) {
  const lines: [string, string][] = [
    ["ルール", ruleTitle],
    ["種別", KIND_LABEL[row.kind] ?? row.kind],
    ["縦横比", row.ratio],
    ["使う場所", row.use],
    ["部品", row.part],
    ["背景", row.background],
    ["見た目", row.look],
    ["描き文字", row.text],
    ["直し", row.fix],
    ["依頼", stampOf(row.requested_at)],
    ["生成", stampOf(row.generated_at)],
    ["失敗の中身", row.state === "failed" ? row.note : ""],
  ];
  return (
    <section className="section">
      <h2 className="section-title">
        {row.name}
        <span className="section-count">{STATE_LABEL[row.state] ?? row.state}</span>
      </h2>
      {row.has_image ? (
        <a className="ig-full" href={imageUrl(row)} target="_blank" rel="noreferrer">
          <img src={imageUrl(row, 640)} alt={row.name} />
        </a>
      ) : (
        <p className="section-note">画像はまだありません。</p>
      )}
      {lines
        .filter(([, value]) => value !== "")
        .map(([label, value]) => (
          <div className="ig-line" key={label}>
            <span>{label}</span>
            <p>{value}</p>
          </div>
        ))}
      <div className="ig-actions">
        <Button onClick={onClose} inline>
          一覧へ戻る
        </Button>
      </div>
    </section>
  );
}

/** 依頼と出来た画像の一覧 */
function ImageList({ say }: { say: (text: string) => void }) {
  const [rows, setRows] = useState<ImageRequest[] | null>(null);
  const [rules, setRules] = useState<RuleSummary[]>([]);
  const [rule, setRule] = useState("*");
  const [state, setState] = useState("*");
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(() => {
    get("op=requests")
      .then((body) => {
        setRows(body.requests as ImageRequest[]);
        setRules(body.rules as RuleSummary[]);
        say("");
      })
      .catch((error) => say(error instanceof Error ? error.message : "読めません"));
  }, [say]);

  useEffect(() => {
    load();
  }, [load]);

  const ruleTitle = (name: string) => (name === "" ? "ルールなし" : rules.find((one) => one.name === name)?.title ?? name);

  if (rows === null) return null;

  const opened = open === null ? null : rows.find((row) => row.name === open) ?? null;
  if (opened) return <RequestDetail row={opened} ruleTitle={ruleTitle(opened.rule)} onClose={() => setOpen(null)} />;

  const usedRules = [...new Set(rows.map((row) => row.rule))].sort();
  const shown = rows.filter((row) => (rule === "*" || row.rule === rule) && (state === "*" || row.state === state));

  return (
    <section className="section">
      <h2 className="section-title">
        画像
        <span className="section-count">
          {shown.length} / {rows.length} 件
        </span>
      </h2>
      <div className="ig-filters">
        <select className="field-input field-select" value={rule} onChange={(event) => setRule(event.target.value)} aria-label="ルール">
          <option value="*">全てのルール</option>
          {usedRules.map((name) => (
            <option value={name} key={name}>
              {ruleTitle(name)}
            </option>
          ))}
        </select>
        <select className="field-input field-select" value={state} onChange={(event) => setState(event.target.value)} aria-label="状態">
          <option value="*">全ての状態</option>
          {Object.entries(STATE_LABEL).map(([key, label]) => (
            <option value={key} key={key}>
              {label}
            </option>
          ))}
        </select>
        <Button onClick={load} inline>
          読み直す
        </Button>
      </div>
      {shown.length === 0 ? <p className="section-note">当てはまる依頼はありません。</p> : null}
      <div className="ig-grid">
        {shown.map((row) => (
          <button type="button" className="ig-card" key={row.name} onClick={() => setOpen(row.name)}>
            <span className="ig-thumb">{row.has_image ? <img src={imageUrl(row, 320)} alt="" loading="lazy" /> : <em>{STATE_LABEL[row.state] ?? row.state}</em>}</span>
            <span className="ig-name">{row.name}</span>
            <span className="ig-meta">
              {ruleTitle(row.rule)}
              {row.state === "done" ? "" : ` ／ ${STATE_LABEL[row.state] ?? row.state}`}
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}

/** ルールの一覧と編集 */
function RuleEditor({ say }: { say: (text: string) => void }) {
  const [rules, setRules] = useState<RuleSummary[] | null>(null);
  const [file, setFile] = useState<RuleFile | null>(null);
  const [draft, setDraft] = useState("");
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const [ask, confirmDialog] = useConfirm();

  const dirty = file !== null && draft !== file.text;

  const load = useCallback(() => {
    get("op=rules")
      .then((body) => setRules(body.rules as RuleSummary[]))
      .catch((error) => say(error instanceof Error ? error.message : "読めません"));
  }, [say]);

  useEffect(() => {
    load();
  }, [load]);

  const openRule = async (name: string) => {
    if (busy) return;
    setBusy(true);
    try {
      const body = (await get(`op=rule&name=${encodeURIComponent(name)}`)) as RuleFile;
      setFile(body);
      setDraft(body.text);
      say("");
    } catch (error) {
      say(error instanceof Error ? error.message : "読めません");
    } finally {
      setBusy(false);
    }
  };

  /** ルールを書く。mtimeMs が null の場合は新しく作る */
  const write = async (name: string, text: string, mtimeMs: number | null) => {
    setBusy(true);
    say("保存しています");
    try {
      const response = await fetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, text, mtimeMs }),
      });
      const body = await response.json();
      if (!response.ok) {
        say(body.error ?? "保存できません");
        return;
      }
      setFile(body as RuleFile);
      setDraft(body.text);
      setNewName("");
      say(mtimeMs === null ? "作りました" : "保存しました");
      load();
    } catch {
      say("サーバーに繋がりません");
    } finally {
      setBusy(false);
    }
  };

  const close = async () => {
    if (dirty && !(await ask("保存前の変更があります。捨てて戻りますか？", { ok: "捨てて戻る", danger: true }))) return;
    setFile(null);
    say("");
  };

  if (file) {
    return (
      <section className="section">
        {confirmDialog}
        <h2 className="section-title">
          {dirty ? "● " : ""}
          {file.title}
          <span className="section-count">{file.name}</span>
        </h2>
        <p className="section-note">1 行目の # の見出しが、ルールの題になります。</p>
        <textarea className="field-input field-textarea ig-editor" value={draft} spellCheck={false} disabled={busy} onChange={(event) => setDraft(event.target.value)} />
        <div className="ig-actions">
          <Button onClick={close} inline>
            一覧へ戻る
          </Button>
          <Button variant="primary" inline disabled={!dirty || busy} onClick={() => write(file.name, draft, file.mtimeMs)}>
            保存
          </Button>
        </div>
      </section>
    );
  }

  if (rules === null) return null;

  return (
    <>
      <section className="section">
        <h2 className="section-title">
          ルール
          <span className="section-count">{rules.length} 件</span>
        </h2>
        <div className="slots">
          {rules.map((one) => (
            <div className="slot" key={one.name}>
              <div className="slot-body">
                <div className="slot-name">{one.title}</div>
                <div className="slot-meta">{one.name}</div>
              </div>
              <div className="slot-actions">
                <Button inline disabled={busy} onClick={() => openRule(one.name)}>
                  編集
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>
      <section className="section">
        <h2 className="section-title">新しいルール</h2>
        <TextField id="ig-new-rule" label="ルールの名" value={newName} onChange={setNewName} note="半角の英小文字・数字・ハイフンで付けます。依頼の rule に書く名になります。" disabled={busy} />
        <div className="ig-actions">
          <Button variant="primary" inline disabled={busy || newName.trim() === ""} onClick={() => write(newName.trim(), `# ${newName.trim()}\n\n## 画風の文\n\n`, null)}>
            作る
          </Button>
        </div>
      </section>
    </>
  );
}

/** 画像生成の管理者用画面。出来た画像を見て、生成のルールを編集する */
export default function ImageGenAdmin({ onBack }: { onBack: () => void }) {
  const [tab, setTab] = useState<"images" | "rules">("images");
  const [message, setMessage] = useState("");

  return (
    <div className="panel panel-wide">
      <Logo />
      <div className="head">
        <span className="head-user">
          開発者用
          <strong>画像生成</strong>
        </span>
        <Button onClick={onBack} inline>
          戻る
        </Button>
      </div>

      <div className="review-tabs ig-tabs">
        <button type="button" className={tab === "images" ? "review-tab review-tab-on" : "review-tab"} onClick={() => setTab("images")}>
          画像
        </button>
        <button type="button" className={tab === "rules" ? "review-tab review-tab-on" : "review-tab"} onClick={() => setTab("rules")}>
          ルール
        </button>
      </div>

      {/* 書きかけのルールを保つため、2 つの欄は両方とも組んだままにする */}
      <div hidden={tab !== "images"}>
        <ImageList say={setMessage} />
      </div>
      <div hidden={tab !== "rules"}>
        <RuleEditor say={setMessage} />
      </div>

      <p className="message message-info">{message}</p>
    </div>
  );
}
